import { env } from '@/env';
import { useEffect, useRef, useState } from 'react';
import { VouchedInstance, VouchedJob, VouchedJobType, VouchedVerification } from 'vouched';
import { IdentityVerificationType } from '@/api/api.schemas';
import { useTranslation } from '@/contexts/i18n-context';
import { TranslationKey } from '@/contexts/translations';
import { Loader } from './loaders/loader';

type JobInfo = {
  initialJobID?: string;
  type: IdentityVerificationType;
};

interface VouchedComponentProps {
  hasSurrogate: boolean;
  personInfo?: VouchedVerification;
  jobInfo: JobInfo;
  onDone: (job: VouchedJob) => void;
}

const translationBase = 'visit.vouched';

type VouchedContent = Record<string, TranslationKey>;

const defaultContent: VouchedContent = {
  crossDeviceInstructions: `${translationBase}.crossDeviceInstructions`,
  middleStartInstructions: `${translationBase}.middleStartInstructions`,
  middleBackIdCapturedInstructions: `${translationBase}.middleBackIdCapturedInstructions`,
  lowerIdCapturedInstructions: `${translationBase}.lowerIdCapturedInstructions`,
  upperStartInstructions: `${translationBase}.upperStartInstructions`,
  cameraButtonLabelContinueSelfie: `${translationBase}.cameraButtonLabelContinueSelfie`,
  crossDeviceSuccess: `${translationBase}.crossDeviceSuccess`,
  mobileHandoffDoneMessage: `${translationBase}.mobileHandoffDoneMessage`,
  mobileHandoffSignOff: `${translationBase}.mobileHandoffSignOff`,
  upperSuccess: `${translationBase}.upperSuccess`,
  success: `${translationBase}.success`,
  lowerSuccess: `${translationBase}.lowerSuccess`,
  review: `${translationBase}.review`,
};

const custodianContent: VouchedContent = {
  crossDeviceInstructions: `${translationBase}.crossDeviceInstructionsSurrogate`,
  upperStartInstructions: `${translationBase}.upperStartInstructionsSelfieSurrogate`,
  selfieVerificationStartInstructions: `${translationBase}.selfieVerificationStartInstructions`,
  startScreenSelfieButtonLabel: `${translationBase}.startScreenSelfieButtonLabel`,
  faceAuthStartInstructions: `${translationBase}.faceAuthStartInstructionsWithSurrogate`,
  crossDeviceSuccess: `${translationBase}.crossDeviceSuccess`,
  mobileHandoffDoneMessage: `${translationBase}.mobileHandoffDoneMessage`,
  mobileHandoffSignOff: `${translationBase}.mobileHandoffSignOff`,
  upperSuccess: `${translationBase}.upperSuccess`,
  success: `${translationBase}.success`,
  lowerSuccess: `${translationBase}.lowerSuccess`,
  review: `${translationBase}.review`,
};

const reverifyContent: VouchedContent = {
  crossDeviceInstructions: `${translationBase}.crossDeviceInstructionsReverify`,
  upperStartInstructions: `${translationBase}.upperStartInstructionsSelfie`,
  faceAuthStartInstructions: `${translationBase}.faceAuthStartInstructionsReverify`,
  startScreenSelfieButtonLabel: `${translationBase}.startScreenSelfieButtonLabel`,
  crossDeviceSuccess: `${translationBase}.crossDeviceSuccess`,
  mobileHandoffDoneMessage: `${translationBase}.mobileHandoffDoneMessage`,
  mobileHandoffSignOff: `${translationBase}.mobileHandoffSignOff`,
  upperSuccess: `${translationBase}.upperSuccess`,
  success: `${translationBase}.successReverify`,
  lowerSuccess: `${translationBase}.lowerSuccess`,
  review: `${translationBase}.review`,
  faceAuthDoneHeader: `${translationBase}.faceAuthDoneHeaderReverify`,
  doneScreenHeader_review: `${translationBase}.doneScreenHeaderReviewReverify`,
  reverifyReviewMessage: `${translationBase}.reverifyReviewMessage`,
};

export function VouchedComponent({
  hasSurrogate,
  personInfo,
  jobInfo,
  onDone,
}: VouchedComponentProps) {
  const id = 'vouched-container';
  const [isReady, setIsReady] = useState(false);
  const vouchedRef = useRef<VouchedInstance>(undefined);

  const { t } = useTranslation();
  const showCustodianText =
    jobInfo.type === IdentityVerificationType.SelfieVerification ||
    (jobInfo.type === IdentityVerificationType.Reverify && hasSurrogate);
  const isIdvFlow = jobInfo.type === IdentityVerificationType.Idv;

  // You must ensure the `Vouched` function is only called once per verification session.
  useEffect(() => {
    // Ensure the script is loaded before this component mounts.
    if (!window.Vouched || vouchedRef.current) {
      return;
    }

    const instance = window.Vouched({
      type: getIdentityVerificationType(jobInfo.type),
      reverificationParameters: jobInfo.initialJobID
        ? {
            jobId: jobInfo.initialJobID,
            match: 'selfie',
          }
        : undefined,
      onReverify: jobInfo.initialJobID ? onDone : undefined,
      liveness: isIdvFlow ? 'enhanced' : 'straight',
      id: 'camera',
      includeBarcode: isIdvFlow,
      appId: env.VITE_VOUCHED_PUBLIC_KEY,
      crossDevice: true,
      crossDeviceQRCode: true,
      crossDeviceSMS: true,
      verification: {
        firstName: personInfo?.firstName,
        lastName: personInfo?.lastName,
        birthDate: personInfo?.birthDate,
        // Billable - set explicitly to false
        enableIPAddress: false,
        enablePhysicalAddress: false,
      },
      content: {
        ...Object.fromEntries(
          Object.entries(
            getVouchedContent(
              showCustodianText,
              jobInfo.type === IdentityVerificationType.Reverify,
            ),
          ).map(([key, value]) => [key, t(value)]),
        ),
        crossDeviceShowOff: false,
      },
      theme: {
        name: 'avant',
        handoffLinkColor: '#00777a',
        baseColor: '#141b41',
        font: 'Raleway',
        common: {
          headings: {
            style: {
              fontSize: '16px',
            },
          },
        },
      },
      onInit: () => {
        setIsReady(true);
      },
      onDone,
    });

    vouchedRef.current = instance;

    instance.mount(`#${id}`);

    return () => {
      instance.unmount(`#${id}`);
    };
    // Dependencies intentionally omitted — including them causes React to re-run the cleanup,
    // which calls instance.unmount() mid-load and cancels the Vouched core.html iframe request,
    // leaving the page in an infinite loading state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!isReady && <Loader type="large" />}
      <div
        id={id}
        style={{
          height: isReady ? '600px' : '1px',
          overflow: 'scroll',
        }}
      />
    </>
  );
}

function getIdentityVerificationType(jobType: IdentityVerificationType): VouchedJobType {
  switch (jobType) {
    case IdentityVerificationType.Idv:
      return 'idv';
    case IdentityVerificationType.SelfieVerification:
      return 'selfie-verification';
    case IdentityVerificationType.Reverify:
      return 'reverify';
  }
}

function getVouchedContent(showCustodianText: boolean, isReverify: boolean): VouchedContent {
  return isReverify ? reverifyContent : showCustodianText ? custodianContent : defaultContent;
}
